/**
 * pages/WorkoutsPage.js
 * 
 * Full workout tracking interface:
 * - List of past workouts
 * - Create/edit modal
 * - Exercise + set logging
 * - Workout stats summary
 */

import React, { useState, useEffect, useCallback } from 'react';
import { workoutsAPI } from '../utils/api';
import { format } from 'date-fns';
import './WorkoutsPage.css';

const CATEGORIES = ['strength', 'cardio', 'flexibility', 'hiit', 'sports', 'other'];

const CategoryBadge = ({ cat }) => {
  const colors = {
    strength: '#6366f1', cardio: '#ef4444', flexibility: '#10b981',
    hiit: '#f59e0b', sports: '#06b6d4', other: '#8b8b9e',
  };
  return (
    <span className="cat-badge" style={{ background: `${colors[cat] || '#8b8b9e'}20`, color: colors[cat] || '#8b8b9e' }}>
      {cat}
    </span>
  );
};

// Workout form state for a new exercise
const emptyExercise = () => ({ name: '', sets: [{ reps: '', weight: '', weight_unit: 'lbs', notes: '' }] });

// Modal for creating / editing a workout
function WorkoutModal({ onClose, onSave, initial }) {
  const [form, setForm] = useState(initial || {
    name: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    duration: '',
    category: 'strength',
    notes: '',
    exercises: [emptyExercise()],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const update = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const addExercise = () => setForm(f => ({ ...f, exercises: [...f.exercises, emptyExercise()] }));
  const removeExercise = (i) => setForm(f => ({ ...f, exercises: f.exercises.filter((_, idx) => idx !== i) }));

  const updateExercise = (i, field, val) => {
    setForm(f => {
      const exs = [...f.exercises];
      exs[i] = { ...exs[i], [field]: val };
      return { ...f, exercises: exs };
    });
  };

  const addSet = (ei) => {
    setForm(f => {
      const exs = [...f.exercises];
      exs[ei] = { ...exs[ei], sets: [...exs[ei].sets, { reps: '', weight: '', weight_unit: 'lbs' }] };
      return { ...f, exercises: exs };
    });
  };

  const updateSet = (ei, si, field, val) => {
    setForm(f => {
      const exs = [...f.exercises];
      const sets = [...exs[ei].sets];
      sets[si] = { ...sets[si], [field]: val };
      exs[ei] = { ...exs[ei], sets };
      return { ...f, exercises: exs };
    });
  };

  const removeSet = (ei, si) => {
    setForm(f => {
      const exs = [...f.exercises];
      exs[ei] = { ...exs[ei], sets: exs[ei].sets.filter((_, idx) => idx !== si) };
      return { ...f, exercises: exs };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return setError('Workout name required');
    setLoading(true);
    setError('');
    try {
      await onSave({
        ...form,
        exercises: form.exercises.filter(ex => ex.name.trim()),
      });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal workout-modal">
        <div className="modal-header">
          <h3>{initial ? 'Edit Workout' : 'Log Workout'}</h3>
          <button className="btn btn-ghost" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message" style={{ marginBottom: '1rem' }}>{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* Basic info */}
          <div className="workout-form-grid">
            <div className="form-group">
              <label className="form-label">Workout Name</label>
              <input className="form-input" value={form.name} onChange={update('name')} placeholder="Push Day, Morning Run..." required />
            </div>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={form.date} onChange={update('date')} required />
            </div>
            <div className="form-group">
              <label className="form-label">Duration (min)</label>
              <input type="number" className="form-input" value={form.duration} onChange={update('duration')} placeholder="60" min="1" />
            </div>
            <div className="form-group">
              <label className="form-label">Category</label>
              <select className="form-input" value={form.category} onChange={update('category')}>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group" style={{ marginBottom: '1.5rem' }}>
            <label className="form-label">Notes</label>
            <textarea className="form-input" value={form.notes} onChange={update('notes')} placeholder="How did it feel?" rows={2} />
          </div>

          {/* Exercises */}
          <div className="exercises-section">
            <div className="flex justify-between items-center" style={{ marginBottom: '1rem' }}>
              <h4 className="text-mono">Exercises</h4>
              <button type="button" className="btn btn-secondary btn-sm" onClick={addExercise}>+ Add Exercise</button>
            </div>

            {form.exercises.map((ex, ei) => (
              <div key={ei} className="exercise-block">
                <div className="exercise-header">
                  <input
                    className="form-input exercise-name-input"
                    value={ex.name}
                    onChange={(e) => updateExercise(ei, 'name', e.target.value)}
                    placeholder={`Exercise ${ei + 1} (e.g., Bench Press)`}
                  />
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeExercise(ei)}>✕</button>
                </div>

                {/* Sets */}
                <table className="sets-table">
                  <thead>
                    <tr>
                      <th>Set</th>
                      <th>Reps</th>
                      <th>Weight</th>
                      <th>Unit</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ex.sets.map((s, si) => (
                      <tr key={si}>
                        <td className="set-num">{si + 1}</td>
                        <td>
                          <input
                            type="number"
                            className="form-input set-input"
                            value={s.reps}
                            onChange={(e) => updateSet(ei, si, 'reps', e.target.value)}
                            placeholder="10"
                            min="0"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            className="form-input set-input"
                            value={s.weight}
                            onChange={(e) => updateSet(ei, si, 'weight', e.target.value)}
                            placeholder="135"
                            min="0"
                            step="0.5"
                          />
                        </td>
                        <td>
                          <select className="form-input set-input" value={s.weight_unit} onChange={(e) => updateSet(ei, si, 'weight_unit', e.target.value)}>
                            <option value="lbs">lbs</option>
                            <option value="kg">kg</option>
                          </select>
                        </td>
                        <td>
                          {ex.sets.length > 1 && (
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => removeSet(ei, si)}>✕</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <button type="button" className="btn btn-ghost btn-sm" style={{ marginTop: '0.5rem' }} onClick={() => addSet(ei)}>
                  + Add Set
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-sm" style={{ marginTop: '1.5rem', justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? <><div className="spinner" /> Saving...</> : 'Save Workout'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function WorkoutsPage() {
  const [workouts, setWorkouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editWorkout, setEditWorkout] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [wData, sData] = await Promise.all([
        workoutsAPI.list({ limit: 20 }),
        workoutsAPI.stats(),
      ]);
      setWorkouts(wData.workouts);
      setStats(sData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSave = async (data) => {
    if (editWorkout) {
      await workoutsAPI.update(editWorkout.id, data);
    } else {
      await workoutsAPI.create(data);
    }
    setEditWorkout(null);
    loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this workout?')) return;
    await workoutsAPI.delete(id);
    loadData();
  };

  return (
    <div className="workouts-page animate-fadein">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Workouts</h1>
          <p className="text-muted">Track your training sessions and progress</p>
        </div>
        <button className="btn btn-primary" onClick={() => { setEditWorkout(null); setShowModal(true); }}>
          + Log Workout
        </button>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="workout-stats-row">
          <div className="ws-item card">
            <div className="ws-num">{stats.this_week}</div>
            <div className="ws-label">This Week</div>
          </div>
          <div className="ws-item card">
            <div className="ws-num">{stats.total_workouts}</div>
            <div className="ws-label">All Time</div>
          </div>
          {stats.by_category?.slice(0, 2).map(c => (
            <div key={c.category} className="ws-item card">
              <div className="ws-num">{c.count}</div>
              <div className="ws-label">{c.category}</div>
            </div>
          ))}
        </div>
      )}

      {/* Workout list */}
      {loading ? (
        <div className="text-muted">Loading workouts...</div>
      ) : workouts.length === 0 ? (
        <div className="empty-state card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>◈</div>
          <h3>No workouts yet</h3>
          <p className="text-muted" style={{ marginBottom: '1rem' }}>Log your first workout to get started</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>Log Workout</button>
        </div>
      ) : (
        <div className="workout-list">
          {workouts.map(w => (
            <div key={w.id} className="workout-card card">
              <div className="workout-card-header">
                <div className="workout-card-main">
                  <h3 className="workout-name">{w.name}</h3>
                  <div className="workout-meta">
                    <span>{format(new Date(w.date + 'T12:00:00'), 'MMM d, yyyy')}</span>
                    {w.duration && <span>{w.duration} min</span>}
                    {w.exercise_count > 0 && <span>{w.exercise_count} exercises</span>}
                    {w.total_sets > 0 && <span>{w.total_sets} sets</span>}
                  </div>
                </div>
                <div className="workout-card-actions">
                  {w.category && <CategoryBadge cat={w.category} />}
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditWorkout(w); setShowModal(true); }}>Edit</button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(w.id)}>Delete</button>
                </div>
              </div>
              {w.notes && <p className="workout-notes">{w.notes}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <WorkoutModal
          onClose={() => { setShowModal(false); setEditWorkout(null); }}
          onSave={handleSave}
          initial={editWorkout}
        />
      )}
    </div>
  );
}
